
//@ts-nocheck
import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { useParams } from "react-router-dom";
import { useAppDispatch } from "../../../app/hooks";
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react'
import { CrossIcon } from "../../icons/Icons";
import { createAppScoreCardDataThunk, fetchScorecardByListDataFilter } from "../../../features/ApplicationData/ApplicationDataSlice";
import { deleteAppScorecardDetails, fetchScoreCardPipelineDetailsData } from "../../../features/scorecard/scoreCardSlice";
import { motion, AnimatePresence } from "framer-motion";
import { getColorForLabel } from "../../../utils/colorUtils";
import CustomMenuDropdown from "../../common/CustomMenuDropdown";


const getRatingColor = (rating: any) => {
  if (!rating) return '#9ca3af';
  const score = parseFloat(rating);
  if (score >= 4) return '#10b981';
  if (score >= 2.5) return '#f59e0b';
  return '#ef4444';
};

const getRatingLabel = (rating: any) => {
  if (!rating) return null;

  const ratingValue = parseFloat(rating);

  if (ratingValue === 5) return "Strong Yes";
  if (ratingValue === 4) return "Yes";
  if (ratingValue === 3) return "Neutral";
  if (ratingValue === 2) return "No";
  if (ratingValue === 1) return "Strong No";

  return null;
};


const getSkillLevelLabelCandidate = (scale: any) => {
  const scaleValue = parseFloat(scale);
  if (scaleValue >= 3.5 && scaleValue <= 4.0) return "Strong Yes";
  if (scaleValue >= 1.5 && scaleValue <= 3.4) return "Yes";
  if (scaleValue >= 0.5 && scaleValue <= 1.4) return "Neutral";
  if (scaleValue >= -1.5 && scaleValue <= 0.4) return "No";
  if (scaleValue >= -2.0 && scaleValue <= -1.6) return "Strong No";

  return null;
};


const getSkillScaleBadgeStylesCandidate = (scale: any) => {

  // Parse the scale value to ensure it's a number
  const scaleValue = parseFloat(scale);

  // Set badge styles based on ranges
  if (scaleValue >= 3.5 && scaleValue <= 4.0) {
    // Expert: Green
    return {
      bg: "bg-green-50",
      text: "text-green-700",
      ring: "ring-green-700/10"
    };
  } else if (scaleValue >= 1.5 && scaleValue <= 3.4) {
    // Proficient: Blue
    return {
      bg: "bg-blue-50",
      text: "text-blue-700",
      ring: "ring-blue-700/10"
    };
  } else if (scaleValue >= 0.5 && scaleValue <= 1.4) {
    // Competent: Yellow
    return {
      bg: "bg-yellow-50",
      text: "text-yellow-700",
      ring: "ring-yellow-700/10"
    };
  } else if (scaleValue >= -1.5 && scaleValue <= 0.4) {
    // Limited: Orange
    return {
      bg: "bg-gray-50",
      text: "text-gray-700",
      ring: "ring-gray-700/10"
    };
  } else if (scaleValue >= -2.0 && scaleValue <= -1.6) {
    // Inadequate: Red
    return {
      bg: "bg-red-50",
      text: "text-red-700",
      ring: "ring-red-700/10"
    };
  } else {
    // Default style for any other values
    return {
      bg: "bg-gray-50",
      text: "text-gray-700",
      ring: "ring-gray-700/10"
    };
  }
};


const getRatingBadgeStyles = (rating: any) => {
  if (!rating) return {
    bg: "bg-gray-50",
    text: "text-gray-700",
    ring: "ring-gray-700/10"
  };

  const ratingValue = parseFloat(rating);

  if (ratingValue === 5) {
    // Strong Yes: Green
    return {
      bg: "bg-green-50",
      text: "text-green-700",
      ring: "ring-green-700/10"
    };
  } else if (ratingValue === 4) {
    // Yes: Light Green
    return {
      bg: "bg-emerald-50",
      text: "text-emerald-700",
      ring: "ring-emerald-700/10"
    };
  } else if (ratingValue === 3) {
    // Neutral: Blue
    return {
      bg: "bg-blue-50",
      text: "text-blue-700",
      ring: "ring-blue-700/10"
    };
  } else if (ratingValue === 2) {
    // No: Orange
    return {
      bg: "bg-orange-50",
      text: "text-orange-700",
      ring: "ring-orange-700/10"
    };
  } else if (ratingValue === 1) {
    // Strong No: Red
    return {
      bg: "bg-red-50",
      text: "text-red-700",
      ring: "ring-red-700/10"
    };
  } else {
    // Default style for any other values
    return {
      bg: "bg-gray-50",
      text: "text-gray-700",
      ring: "ring-gray-700/10"
    };
  }
};



const CircularRatingProgress = ({ rating, maxRating = 5, size = 36, strokeWidth = 5, color = '#3b82f6' }) => {
  // Handle null or undefined ratings and format to 1 decimal place
  const actualRating = rating ? parseFloat(rating).toFixed(1) : '0.0';

  // Calculate percentage
  const percentage = (parseFloat(actualRating) / maxRating) * 100;

  // Calculate circle properties
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  // Center position
  const center = size / 2;

  return (
    <div className="flex items-center justify-center bg-white rounded-lg">
      <div className="relative" style={{ width: size, height: size }}>
        {/* Background circle */}
        <svg width={size} height={size}>
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="#e6e6e6"
            strokeWidth={strokeWidth}
          />
          {/* Progress circle */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            transform={`rotate(-90 ${center} ${center})`}
          />
        </svg>

        {/* Score text */}
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#00005506]" >
          <span className="text-[10px] leading-4 font-normal" style={{ color }}>
            {actualRating}
          </span>
        </div>
      </div>
    </div>
  );
};

const ratingOptions1 = [
  { value: 1, label: "Strong No" },
  { value: 2, label: "No" },
  { value: 3, label: "Neutral" },
  { value: 4, label: "Yes" },
  { value: 5, label: "Strong Yes" },
];


const RatingButton1 = ({ label, value, selectedValue, onClick }: any) => {
  const isSelected = selectedValue == value;

  return (
    <button
      className={`flex items-center gap-[6px] text-[13px] rounded-md px-2 py-1 text-xs font-medium capitalize transition-colors ${isSelected
        ? 'bg-indigo-600 text-white ring-1 ring-inset ring-indigo-600'
        : 'bg-gray-50 text-gray-600 ring-1 ring-inset ring-gray-500/10'
        }`}
      onClick={() => onClick(value)}
      type="button"
    >
      {label}
    </button>
  );
};


const AutoResizeTextarea1Candidate = ({ value, onChange, placeholder, hasError }: any) => {
  const textareaRef = useRef(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.max(60, textareaRef.current.scrollHeight)}px`;
    }
  }, [value]);

  return (
    <div className="w-full">
      <textarea
        ref={textareaRef}
        className={`outline-none bg-transparent focus:outline-none focus:ring-0 text-[14px] text-dark-black-100 w-full m-0 placeholder:text-[#9e9ea0]
        border-1 border-solid ${hasError ? 'border-red-500' : 'border-light-gray-200'} rounded-md p-2 min-h-[60px] overflow-hidden resize-none leading-5`}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        rows={1}
        maxLength={1000}
      />
      {hasError && <p className="text-red-500 text-xs mt-1">This field is required</p>}
    </div>
  );
};

function ScoreCardApp({ job: jobProp, app: appProp, quickData, applicationId1 }: any) {
  const [scorecard, setScorecard] = useState([]);
  const [loading, setLoading] = useState(true);
  const { baseId, jobId: jobIdFromParams, applicationId: applicationIdFromParams, skillId } = useParams<{
    baseId: string;
    jobId: string;
    applicationId: string;
    skillId: string
  }>();

  const jobId = jobProp || jobIdFromParams;
  const applicationId = appProp || applicationIdFromParams || quickData || applicationId1

  const dispatch = useAppDispatch();
  const [page, setPage] = useState(1);
  const userDetailsData = localStorage.getItem('jobByHiringTeamMembers');
  const baseMembers = userDetailsData ? JSON.parse(userDetailsData) : [];
  const [pipelineStages, setPipelineStages] = useState([]);
  const [openAccordionIds, setOpenAccordionIds] = useState(() => {
    const initialState = {};
    if (scorecard && scorecard.skill) {
      scorecard.skill.forEach(item => {
        initialState[item.id] = true;
      });
    }
    return initialState;
  });
  const [openAccordionIds1Candidate, setOpenAccordionIds1Candidate] = useState({});
  const [overallRating, setOverallRating] = useState(null);
  const [overallComment, setOverallComment] = useState("");
  const [appScorecardCandidateOpen, setAppScorecardCandidateOpen] = useState(false)
  const [overallRatingError, setOverallRatingError] = useState(false);
  const [savingData, setSavingData] = useState(false);
  const [openAccordionIds1CandidateCreate, setOpenAccordionIds1CandidateCreate] = useState({});
  const [editingScorecard, setEditingScorecard] = useState(null);
  const [openDeleteModalExpCandidate, setOpenDeleteModalExpCandidate] = useState(false)
  const [deleteLoadingExpCandidate, setDeleteLoadingExpCandidate] = useState(false)
  const [selectedIndexCandidate, setSelectedIndexCandidate] = useState(null);
  const [appScoreCardId, setAppScoreCardId] = useState(null);



  const getStageName = (stageId: any) => {
    const scorecardData = JSON.parse(localStorage.getItem("jobPipelineStageData") || "{}");
    const stages = scorecardData?.stages || [];
    const stage = stages.find(stage => stage.id === stageId);
    return stage ? stage.stage_name : null;
  };


  const getUserName = (userId: any) => {
    if (userId === 0 || userId === "0") {
      return "AI";
    }
    const user = baseMembers.find(member => member.user_id === userId || member.user_id === Number(userId));
    return user ? user.name : `User ${userId}`;
  };

  const getUserImage = (userId: any) => {
    if (userId === 0 || userId === "0") {
      return null;
    }
    const user = baseMembers.find(member => member.user_id === userId || member.user_id === Number(userId));
    return user ? user.profile_pic : null;
  };


  useEffect(() => {
    if (baseId) {
      const fetchJob = async () => {
        const storedBase = localStorage.getItem("jobPipelineStageData");
        if (storedBase) {
          try {
            const parsedData = JSON.parse(storedBase);
            if (parsedData && parsedData.data && parsedData.data.stages) {
              setPipelineStages(parsedData.data.stages);
            }
          } catch (error) {
            console.error("Error parsing local storage data:", error);
          }
        } else {
          try {
            const res = await dispatch(
              fetchScoreCardPipelineDetailsData({
                baseId: Number(baseId),
                JobId: jobId
              })
            );
            if (res && res.data) {
              setPipelineStages(res?.data?.stages);
            }
          } catch (error) {
            console.error("Error fetching base member details:", error);
          }
        }
      };
      fetchJob();
    }
  }, [baseId, dispatch, jobId]);


  useEffect(() => {
    const fetchScorecard = async () => {
      if (baseId && applicationId) {
        setLoading(true);
        try {
          const res = await dispatch(
            fetchScorecardByListDataFilter({
              baseId: Number(baseId),
              appId: applicationId
            })
          ).unwrap();
          if (res.status === 200) {
            setScorecard(res.data);

            const initialOpenState = res.data.skill.reduce((acc, scorecard) => {
              acc[scorecard.id] = true;
              return acc;
            }, {});
            setOpenAccordionIds(initialOpenState);
          }
        } catch (error) {
          console.error("Error fetching scorecard:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchScorecard();
  }, [baseId, applicationId, dispatch]);


  const handleAppCandidateRating = (id, appScorecard, scorecardId) => {
    const scorecardToEdit = appScorecard;
    setOverallRating(scorecardToEdit.rating);
    setOverallComment(scorecardToEdit.comment || "");
    setEditingScorecard(scorecardToEdit);
    setAppScoreCardId(scorecardId)
    setOpenAccordionIds1Candidate((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
    setOpenAccordionIds1CandidateCreate(false)
  };



  const handleOverallRatingSelect = (value) => {
    setOverallRating(value);
  };

  const handleOverallCommentChange = (e) => {
    setOverallComment(e.target.value);
  };

  const handleEditClickCandidate = (id) => {
    const scorecardToEdit = scorecard.candidate
      .flatMap(card => card.appScorecards || [])
      .find(sc => sc.id === id);

    if (scorecardToEdit) {
      setOverallRating(scorecardToEdit.rating);
      setOverallComment(scorecardToEdit.comment || "");
      setEditingScorecard(scorecardToEdit);
    }

    setAppScorecardCandidateOpen(true);
    setOpenAccordionIds1Candidate((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
    setOpenAccordionIds1CandidateCreate(false)
  }

  const handleCreateCandidate = (id, data) => {
    const scorecardToEdit = data;
    setOverallRating("");
    setOverallComment("");
    setEditingScorecard("");
    setAppScoreCardId(id)

    setOpenAccordionIds1CandidateCreate((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
    setOpenAccordionIds1Candidate(false)
  }


  const handleSaveScorecardCandidateCreate = async (data) => {

    if (overallRating === null) {
      setOverallRatingError(true);
      return;
    }

    setOverallRatingError(false);

    setSavingData(true);
    try {
      const payload = {
        app_id: applicationId,
        scorecard_id: data.id || skillId,
        comment: overallComment,
        rating: overallRating

      };

      const response = await dispatch(createAppScoreCardDataThunk({
        baseId: Number(baseId),
        data: payload
      }));

      if (response?.payload.status === 200) {
        toast.success("Candidate app scorecard saved successfully!");
        setOverallComment('')
        const res = await dispatch(
          fetchScorecardByListDataFilter({
            baseId: Number(baseId),
            appId: applicationId,
          })
        ).unwrap();
        if (res.status === 200) {
          setScorecard(res.data);
          setOpenAccordionIds1CandidateCreate(false)
        }

      } else {
        if (response?.error?.message) {
          toast.error('Rating should be selected' || `Error: ${response.error.message}`);
        } else {
          throw new Error("Failed to save candidate app scorecard");
        }
      }
    } catch (error) {
      console.error("Error saving scorecard:", error);
      toast.error("Failed to save app scorecard. Please try again.");
    } finally {
      setSavingData(false);
    }
  };


  const handleSaveScorecardCandidateUpdate = async () => {
    if (overallRating === null) {
      setOverallRatingError(true);
      return;
    }

    setOverallRatingError(false);
    setSavingData(true);
    try {
      const payload = {
        app_id: applicationId,
        scorecard_id: editingScorecard ? editingScorecard.scorecard_id || appScoreCardId : skillId,
        comment: overallComment,
        rating: overallRating
      };

      if (editingScorecard) {
        payload.id = editingScorecard.id;
      }

      const response = await dispatch(createAppScoreCardDataThunk({
        baseId: Number(baseId),
        data: payload
      }));

      if (response?.payload) {
        toast.success("Candidate app scorecard saved successfully!");
        setOverallComment('');
        setOverallRating(null);
        setEditingScorecard(null);

        if (editingScorecard) {
          setOpenAccordionIds1Candidate((prev) => ({
            ...prev,
            [editingScorecard.id]: false,
          }));
        }

        const res = await dispatch(
          fetchScorecardByListDataFilter({
            baseId: Number(baseId),
            appId: applicationId
          })
        ).unwrap();
        if (res.status === 200) {
          setScorecard(res.data);
        }
      } else {
        if (response?.error?.message) {
          toast.error(`Error: ${response.error.message}`);
        } else {
          throw new Error("Failed to save candidate app scorecard");
        }
      }
    } catch (error) {
      console.error("Error saving scorecard:", error);
      toast.error("Failed to save app scorecard. Please try again.");
    } finally {
      setSavingData(false);
    }
  };


  const handleDeleteClickCandidate = (appScoreCardId) => {
    setOpenDeleteModalExpCandidate(true)
    setSelectedIndexCandidate(appScoreCardId)
  }

  const handleCloseModalCandidate = () => {
    setOpenDeleteModalExpCandidate(false);
  };

  const handleDeleteConfirmCandidate = async (appScorecard) => {

    setDeleteLoadingExpCandidate(true);

    try {
      await dispatch(
        deleteAppScorecardDetails({
          baseId: Number(baseId),
          id: selectedIndexCandidate?.id,
        })
      ).unwrap();

      toast.success("Candidate scorecard deleted successfully!");
      setOpenDeleteModalExpCandidate(false);


      const res = await dispatch(
        fetchScorecardByListDataFilter({
          baseId: Number(baseId),
          appId: applicationId
        })
      ).unwrap();

      if (res.status === 200) {
        setScorecard(res.data);
      }
    } catch (error) {
      toast.error("Failed to delete candidate scorecard!");
    } finally {
      setDeleteLoadingExpCandidate(false);
    }
  };



  return (
    <div>
      <div className="bg-[#e0e0e0] w-full h-[1px] mt-8 mb-6"></div>
      <div className="flex items-center gap-x-3 mt-6 mb-6">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={24}
          height={24}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="icon icon-tabler icons-tabler-outline icon-tabler-users w-[1rem] h-[1rem] text-[#808080]"
        >
          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
          <path d="M9 7m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0" />
          <path d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          <path d="M21 21v-2a4 4 0 0 0 -3 -3.85" />
        </svg>
        <span className="font-medium text-dark-black-200  mr-1 text-[15px] flex items-center gap-[6px] ">
          Candidate
        </span>
      </div>



      <div className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center relative z-50" style={{ height: "100px" }}>
            <div className="simple-spinner-loader">
              <span></span>
            </div>
          </div>


        ) : scorecard?.candidate?.length === 0 ? (
          <div className="w-full flex h-[100px] justify-center items-center flex-col gap-y-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={24}
              height={24}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="icon icon-tabler icons-tabler-outline icon-tabler-info-triangle  w-[1.5rem] h-[1.5rem] text-[#808080]"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z" />
              <path d="M12 9h.01" />
              <path d="M11 12h1v4h1" />
            </svg>
            <p className="text-[#0000007a] text-[13px]">No results found</p>
          </div>


        ) : (
          [...scorecard.candidate].map((msg, index) => {

            const stageId = msg?.appScorecards?.find(item => item.stage_id)?.stage_id || msg.stage_id;
            const stageName = getStageName(stageId);


            const isAccordionOpenCandidateCreate = openAccordionIds1CandidateCreate[msg.id] || false;


            return (
              <>

                <div className="rounded-md cursor-pointer ring-1 ring-gray-200 feedback-delete-hover">
                  <div className="flex items-center bg-[#00005506]">
                    <div className="w-[80%] flex flex-col gap-1 px-4 pt-[15px] pb-[15px]">
                      <div className="flex justify-start items-center gap-x-2">
                        <span className="flex items-center gap-x-3">
                          <span className="flex h-[8px] w-[8px] shrink-0 items-center justify-center rounded-[2px] border-none text-[0.625rem] font-medium text-white capitalize"
                            style={{ backgroundColor: msg.scorecard_colour }}
                          >
                          </span>
                        </span>
                        <div className="flex gap-x-[6px] items-center">
                          <span className="text-[14px] text-[#374151] font-medium leading-4 capitalize">
                            {
                              msg.scorecard_name
                            }
                          </span>
                        </div>
                      </div>
                      <p className="font-normal text-[#374151] text-[13px] wg-antialiased leading-4">
                        {msg.scorecard_description}
                      </p>
                    </div>
                    <div className="w-[20%] pr-4 mr-0 flex items-center gap-x-2 justify-end">


                      <div className="flex shrink-0 pr-2">
                        <div
                          className="h-[36px] w-[36px] rounded-[8px] flex flex-row items-center justify-center text-white"

                        >
                          <CircularRatingProgress
                            rating={msg?.appScorecards[0]?.rating_average}
                            color={getRatingColor(msg?.appScorecards[0]?.rating_average)}
                          />
                        </div>
                      </div>


                      <span className="table-tooltip before:!bottom-[28px] before:!left-[38px]"
                        data-title='score candidate'>
                        <button
                          onClick={() => handleCreateCandidate(msg.id, msg)}
                          className=" rounded-md relative inline-flex hover:bg-[#f4f4f5] items-center justify-center whitespace-nowrap text-sm font-medium transition-colors
                                       outline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring/70 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none 
                                       [&_svg]:shrink-0 border border-input bg-background shadow-sm shadow-black/5 hover:bg-accent hover:text-accent-foreground h-6 text-[#808080] w-auto py-1 px-2 gap-x-2">
                          Score
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="icon icon-tabler icons-tabler-outline icon-tabler-arrow-narrow-right w-[0.9rem] h-[0.9rem] text-[#808080]"
                          >
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <path d="M5 12l14 0" />
                            <path d="M15 16l4 -4" />
                            <path d="M15 8l4 4" />
                          </svg>
                        </button>
                      </span>
                    </div>
                  </div>


                  <AnimatePresence>
                    {isAccordionOpenCandidateCreate && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        style={{ overflow: 'hidden' }}
                      >
                        <div className='bg-[#e0e0e0] w-full h-[1px]'></div>
                        <div className="flex items-center gap-2 my-3 flex-wrap px-4 pt-1">
                          {ratingOptions1.map((option) => (
                            <RatingButton1
                              key={option.value}
                              label={option.label}
                              value={option.value}
                              selectedValue={overallRating}
                              onClick={handleOverallRatingSelect}
                            />
                          ))}
                        </div>
                        {overallRatingError && (
                          <p className="text-red-500 text-xs mb-2 mt-[-6px]">Please select an overall rating</p>
                        )}

                        <div className="px-4 pb-3">
                          <AutoResizeTextarea1Candidate
                            value={overallComment}
                            onChange={handleOverallCommentChange}
                            placeholder="Write an overall comment about this candidate..."
                          />
                        </div>

                        <div className='mt-0 pb-4 flex justify-end px-4'>
                          <button
                            className="group inline-flex shrink-0 select-none justify-end leading-6
                             transition-colors duration-100 focus:outline-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 
                             disabled:pointer-events-none gap-0 items-center px-[14px] py-1 text-[13px] font-medium h-[32px] rounded-md text-white bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-indigo-600"
                            type="button"
                            onClick={() => handleSaveScorecardCandidateCreate(msg)}
                          >
                            <div className='flex items-center gap-[6px]'>
                              Save
                              {savingData && (
                                <div className="simple-spinner">
                                  <span></span>
                                </div>
                              )}
                            </div>
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>



                  {msg.appScorecards && msg.appScorecards.length > 0 && (
                    <>
                      <div className='bg-[#e0e0e0] w-full h-[1px] mb-0'></div>
                      <div className="w-full">
                        {msg.appScorecards.slice(0, 1).map((appScorecard, idx) => {
                          const styles = getSkillScaleBadgeStylesCandidate(appScorecard.rating_average);
                          return (
                            <div key={idx} className="px-4 py-2 flex items-center justify-between">
                              <div className="text-[14px] leading-4 text-gray-500">
                                {msg.appScorecards ? msg.appScorecards.filter(scorecard => scorecard.status === 'done').length : 0} ratings
                              </div>
                              {appScorecard.rating_average && (
                                <span
                                  className={`inline-flex items-center rounded-md ${styles.bg} px-2 py-1 text-xs font-normal ${styles.text} ring-1 ring-inset ${styles.ring}`}
                                >
                                  {getSkillLevelLabelCandidate(appScorecard.rating_average)}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>



                      <div className="w-full border-t border-[#e0e0e0] last-of-type:border-b-0">
                        {msg.appScorecards.map((appScorecard, idx) => {
                          const reviewerName = getUserName(appScorecard.assigned_to);
                          const reviewerImage = getUserImage(appScorecard.assigned_to)
                          const styles1 = getRatingBadgeStyles(appScorecard.rating);
                          const isAccordionOpenCandidate = openAccordionIds1Candidate[appScorecard.id] || false;

                          const dropdownGroups = [
                            {
                              options: [
                                {
                                  label: `Edit`,
                                  className:
                                    "py-[0.375rem] px-[0.75rem] bg-white text-[#000000eb] text-[13px] hover:bg-[#e7e7e8]",
                                  onClick: () => handleEditClickCandidate(appScorecard.id),
                                },
                                {
                                  label: `Delete`,
                                  className:
                                    "py-[0.375rem] px-[0.75rem] bg-white text-[#000000eb] text-[13px] hover:bg-[#e7e7e8]",
                                  onClick: () => handleDeleteClickCandidate(appScorecard),
                                },
                              ],
                            },
                          ];

                          return (
                            <div key={idx} className="flex items-center gap-x-2 border-b border-light-gray-200 last-of-type:border-b-0">


                              <div key={appScorecard.id || idx} className="w-full">
                                <ul className="list-none w-full  px-4">
                                  <li className="flex flex-row items-center justify-between text-[0.875rem] px-[0rem] py-4 w-full">

                                    <div className="flex-1 px-0 flex flex-col gap-y-[4px]">
                                      <div className="flex items-center gap-x-2 mt-[-2px] justify-between">
                                        <div className="flex items-center gap-x-2">
                                          <span className="flex items-center gap-x-3">
                                            {
                                              reviewerImage ? (
                                                <img
                                                  src={`${process.env.REACT_APP_API_BASE_URL}/${reviewerImage}`}
                                                  alt={`${reviewerName || 'Profile'}`}
                                                  className="h-6 w-6 flex-none rounded-full ring-1 ring-gray-900/10"
                                                />
                                              ) : (
                                                <div style={{ backgroundColor: getColorForLabel(reviewerName.charAt(0) || '') }}
                                                  className="w-6 h-6 rounded-full flex items-center justify-center leading-5">
                                                  <span className="font-medium text-white leading-6"
                                                  >{reviewerName.charAt(0)}</span>
                                                </div>
                                              )
                                            }
                                          </span>
                                          <p className="text-[0.8125rem] font-medium leading-4 text-[#1b1b1b] capitalize">
                                            {reviewerName}
                                          </p>
                                        </div>
                                        <div className="flex items-center gap-x-2">
                                          {
                                            appScorecard.rating &&
                                            <span
                                              className={`inline-flex items-center rounded-md ${styles1.bg} px-2 py-1 text-xs font-normal ${styles1.text} ring-1 ring-inset ${styles1.ring}`}
                                            >
                                              {getRatingLabel(appScorecard.rating)}
                                            </span>
                                          }
                                          <div className='ml-[-4px]'>
                                            <div className="[&>div]:shrink">
                                              <CustomMenuDropdown
                                                sections={dropdownGroups}
                                                icon={
                                                  <svg
                                                    stroke="currentColor"
                                                    fill="none"
                                                    strokeWidth="2"
                                                    viewBox="0 0 24 24"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    aria-hidden="true"
                                                    focusable="false"
                                                    height="1em"
                                                    width="1em"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                  >
                                                    <circle cx="12" cy="12" r="1"></circle>
                                                    <circle cx="12" cy="5" r="1"></circle>
                                                    <circle cx="12" cy="19" r="1"></circle>
                                                  </svg>
                                                }
                                                menuItemsClassName="left-auto"
                                              />
                                            </div>
                                          </div>
                                        </div>
                                      </div>


                                      {
                                        appScorecard.comment &&
                                        <p className="text-[0.8125rem] font-normal leading-4 text-[#5c5c5e] ml-[30px]">
                                          "{appScorecard.comment.trim()}"
                                        </p>
                                      }


                                    </div>
                                    <div className="flex gap-1 items-center">
                                      {
                                        (appScorecard.status === 'pending') &&
                                        <span className="table-tooltip before:!bottom-[28px] before:!left-[8px]"
                                          data-title='skills'>
                                          <button
                                            onClick={() => handleAppCandidateRating(appScorecard.id, msg, appScorecard.scorecard_id)}
                                            className=" rounded-md relative inline-flex hover:bg-[#f4f4f5] items-center justify-center whitespace-nowrap text-sm font-medium transition-colors
                                       outline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring/70 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none 
                                       [&_svg]:shrink-0 border border-input bg-background shadow-sm shadow-black/5 hover:bg-accent hover:text-accent-foreground h-6 w-6">
                                            <motion.div
                                              animate={{ rotate: isAccordionOpenCandidate ? 180 : 0 }}

                                              transition={{ duration: 0.3 }}
                                            >
                                              <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width={16}
                                                height={16}
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth={2}
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-down w-[0.9rem] h-[0.9rem] text-[#808080]"
                                              >
                                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                <path d="M6 9l6 6l6 -6" />
                                              </svg>
                                            </motion.div>
                                          </button>

                                        </span>
                                      }
                                    </div>
                                  </li>
                                </ul>
                                <AnimatePresence>
                                  {isAccordionOpenCandidate && (
                                    <motion.div
                                      initial={{ height: 0, opacity: 0 }}
                                      animate={{ height: "auto", opacity: 1 }}
                                      exit={{ height: 0, opacity: 0 }}
                                      transition={{ duration: 0.3 }}
                                      style={{ overflow: 'hidden' }}
                                    >
                                      <div className='bg-[#e0e0e0] w-full h-[1px]'></div>
                                      <div className="flex items-center gap-2 my-3 flex-wrap px-4 pt-1">
                                        {ratingOptions1.map((option) => (
                                          <RatingButton1
                                            key={option.value}
                                            label={option.label}
                                            value={option.value}
                                            selectedValue={overallRating}
                                            onClick={handleOverallRatingSelect}
                                          />
                                        ))}
                                      </div>
                                      {overallRatingError && (
                                        <p className="text-red-500 text-xs mb-2 mt-[-6px]">Please select an overall rating</p>
                                      )}

                                      <div className="px-4 pb-3">

                                        <AutoResizeTextarea1Candidate
                                          value={overallComment}
                                          onChange={handleOverallCommentChange}
                                          placeholder="Write an overall comment about this candidate..."
                                        />
                                      </div>

                                      <div className='mt-0 pb-4 flex justify-end px-4'>
                                        <button
                                          className="group inline-flex shrink-0 select-none  justify-end text-sm leading-6 
                                          transition-colors duration-100 focus:outline-0 focus-visible:outline focus-visible:outline-2 
                                          focus-visible:outline-offset-2 disabled:pointer-events-none gap-0 items-center px-[14px] py-1 text-[13px] font-medium h-[32px] rounded-md text-white
                                           bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-indigo-600"
                                          type="button"
                                          onClick={handleSaveScorecardCandidateUpdate}
                                        >
                                          <div className='flex items-center gap-[6px]'>
                                            Save
                                            {savingData && (
                                              <div className="simple-spinner">
                                                <span></span>
                                              </div>
                                            )}
                                          </div>
                                        </button>
                                      </div>
                                    </motion.div>
                                  )}
                                </AnimatePresence>

                              </div>

                            </div>

                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              </>

            );
          })
        )}
      </div>



      {/* candidate  */}
      {openDeleteModalExpCandidate && (
        <Dialog open={true} onClose={handleCloseModalCandidate} className="relative z-10">
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
          />
          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <DialogPanel
                transition
                className="relative transform rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
              >
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    onClick={handleCloseModalCandidate}
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  >
                    <span className="sr-only">Close</span>
                    <CrossIcon />
                  </button>
                </div>
                <div className="flex flex-col gay-y-4">
                  <span className="leading-6 text-sm text-gray-500">
                    Do you want to sure delete this candidate scorecard?
                  </span>
                </div>
                <div className="mt-5 sm:mt-4 flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={handleCloseModalCandidate}
                    className=" inline-flex leading-6 w-fit justify-center rounded-md bg-white items-center px-[14px] py-1 text-[13px] font-medium h-[32px] text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteConfirmCandidate}
                    type="button"
                    className="rounded-md bg-indigo-600 items-center px-[14px] py-1 text-[13px] font-medium h-[32px] text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 "
                  >
                    {deleteLoadingExpCandidate ? (
                      <div className='flex items-center gap-[6px]'>
                        Delete
                        <div className="simple-spinner">
                          <span></span>
                        </div>
                      </div>
                    ) : (
                      'Delete'
                    )}
                  </button>
                </div>
              </DialogPanel>
            </div>
          </div>
        </Dialog>
      )}

    </div>
  );
}

export default ScoreCardApp;